import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CircleCheck,
  Loader2,
  RefreshCw,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import {
  emptyFilter,
  type ChannelWrite,
  type Filter,
  type JsonObject,
  type Sort,
  type SyncEventsQuery,
  type SyncResourceStatus,
  type SyncStatus,
  type WebhookEventStatus,
  type WebhookHealth,
  type ChannelWriteStatus,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import {
  LoadFailed,
  NoMatches,
  NothingYet,
} from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { toggleSort } from "@/components/table/sorting";
import { Badge } from "@/components/ui/badge";
import { SELECTED_CHOICE } from "@/components/list/selectedChoice";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CHANNEL_WRITE_FIELDS } from "@/modules/settings/fields";
import { TrackingProblems } from "@/modules/settings/logistics/TrackingProblems";
import { projectChannelWrite } from "@/modules/settings/projections";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { useOpenRecord } from "@/routes/useOpenRecord";

function formatTimestamp(value: string | null): string {
  if (value === null) return "Never";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

const STATUS_LABEL: Record<ChannelWriteStatus, string> = {
  failed: "Failed",
  pending: "Pending",
  succeeded: "Succeeded",
};

function statusBadgeVariant(
  status: ChannelWriteStatus,
): "destructive" | "outline" | "secondary" {
  if (status === "failed") return "destructive";
  if (status === "pending") return "outline";
  return "secondary";
}

type StatusTab = "all" | ChannelWriteStatus;

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "failed", label: "Failed" },
  { value: "pending", label: "Pending" },
  { value: "succeeded", label: "Succeeded" },
];

/**
 * The one condition this screen ever builds — `status is <tab>` — kept as a
 * function rather than four hand-written literals so the "all" tab and the
 * three real statuses share one shape and can't drift out of sync with
 * `StatusTab` if a fourth status is ever added to `ChannelWriteStatus`.
 */
function filterForStatusTab(tab: StatusTab): Filter {
  if (tab === "all") return emptyFilter();
  return {
    kind: "group",
    combinator: "and",
    children: [
      { kind: "condition", field: "status", operator: "is", value: tab },
    ],
  };
}

interface ChannelWriteCounts {
  all: number | null;
  failed: number | null;
  pending: number | null;
}

/**
 * `succeeded` is derived rather than fetched — a fourth `limit: 1` read would
 * buy nothing `all - failed - pending` doesn't already answer, as long as
 * all three are known. Any of them coming back `null` (a real backend that
 * cannot count cheaply, per `Page.total`'s doc comment) means the derived
 * figure would be a guess dressed as a number, so it stays `null` too rather
 * than print something that looks exact and isn't.
 */
function countFor(
  counts: ChannelWriteCounts | undefined,
  tab: StatusTab,
): number | null {
  if (!counts) return null;
  if (tab === "all") return counts.all;
  if (tab === "failed") return counts.failed;
  if (tab === "pending") return counts.pending;
  if (
    counts.all === null ||
    counts.failed === null ||
    counts.pending === null
  ) {
    return null;
  }
  return counts.all - counts.failed - counts.pending;
}

/**
 * The channel's raw payload, collapsed (see `orders/screens/OrderRawPayload`
 * for the same technique). `request` and `response` carry the customer names,
 * phone numbers and addresses that were in the write itself, so this defaults
 * closed with no JavaScript required to keep it that way — a plain
 * `useState(false)` toggle would not survive a future edit the same way a
 * `<details>` element does.
 */
function RawPayloadDetails({
  label,
  value,
}: {
  label: string;
  value: JsonObject | null;
}) {
  return (
    <details>
      <summary className="text-muted-foreground cursor-pointer text-sm select-none">
        {label}
      </summary>
      {value === null ? (
        <p className="text-muted-foreground mt-2 text-sm">
          Not answered yet — the write is still pending.
        </p>
      ) : (
        <pre className="bg-muted mt-2 max-h-96 overflow-auto rounded-md p-3 text-xs">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </details>
  );
}

function ChannelWriteDetail({
  write,
  actorName,
  onBack,
}: {
  write: ChannelWrite;
  actorName: string;
  onBack?: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {onBack ? (
        <div className="shrink-0 border-b px-4 py-2">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to the log
          </Button>
        </div>
      ) : null}
      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{write.mutation}</h3>
          <Badge
            variant={statusBadgeVariant(write.status)}
            className="font-normal"
          >
            {STATUS_LABEL[write.status]}
          </Badge>
        </div>
        <p className="text-muted-foreground text-sm">
          {write.entityType} {write.entityId} · {actorName} ·{" "}
          {new Date(write.createdAt).toLocaleString("en-IN")}
        </p>

        {write.error ? (
          <div>
            <div className="text-muted-foreground text-xs">
              The channel's own words
            </div>
            {/* Verbatim, not paraphrased, not truncated — see the doc
                comment on `ChannelWrite.error` in `packages/domain`. */}
            <p className="text-destructive text-sm">{write.error}</p>
          </div>
        ) : null}

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Completed</dt>
          <dd className="tabular-nums">
            {write.completedAt
              ? new Date(write.completedAt).toLocaleString("en-IN")
              : "Not yet"}
          </dd>
          <dt className="text-muted-foreground">Fingerprint</dt>
          {/* (entity, normalised payload) — what stops the ERP's own write
              from being re-ingested as a webhook echo. It matters for
              exactly that debugging moment and not otherwise, which is why
              it lives here in muted monospace rather than as a column of
              its own. */}
          <dd className="text-muted-foreground font-mono text-xs">
            {write.fingerprint}
          </dd>
        </dl>

        <RawPayloadDetails label="Show what was sent" value={write.request} />
        <RawPayloadDetails label="Show what came back" value={write.response} />
      </div>
    </div>
  );
}

/**
 * Every write the ERP has attempted at Shopify (#270 item 3, FR-SYN).
 *
 * CLAUDE.md: "expected failures are rows, not logs" — this is the row.
 * `SettingsRepository.listChannelWrites` reads `channel_writes`, which
 * `writeThrough()` fills (#164, #173): every order tag, note and cancel the
 * ERP has sent, succeeded or failed, and the channel's own words for a
 * failure. The detail drawer shows what was sent, what came back and why.
 *
 * Not registered as `sync`'s `SectionUi.fields` in `index.ts` (see
 * `CHANNEL_WRITE_FIELDS`'s doc comment for why), so this owns its filter,
 * sort and pagination state locally instead of taking it from the
 * URL-driven toolbar — the same `DataTable`, `Pagination`, `TableSkeleton`
 * and empty states every list uses, just without that toolbar around it.
 *
 * Findability: succeeded writes should far outnumber failed ones, which is
 * the ordinary case, not a crisis — so failures are made findable rather
 * than made loud. A status tab carries every tab's count up front (clicking
 * "Failed" is not the only way to learn there are fifteen), and
 * within any tab the `status` column is a colour-coded badge so a refusal
 * reads red without anyone needing to filter at all. Ordering is left alone
 * at newest-first — the same default `listChannelWrites` documents for every
 * other consumer of it — rather than reordered to put failures first, which
 * would make this screen's default order disagree with the repository's.
 */
function ChannelWriteLog() {
  const data = useDataSource();

  const [statusTab, setStatusTab] = useState<StatusTab>("all");
  const [sort, setSort] = useState<Sort[]>([]);
  const [limit, setLimit] = useState(25);
  // The tab and the sort are this screen's own state, not the URL's, so the
  // trail is told about both: a sort is a different order of the same rows,
  // and page three of one is not page three of another.
  const [history, setHistory] = useCursorHistory(statusTab, sort);
  const cursor = history[history.length - 1];
  const { recordId, open } = useOpenRecord();

  /*
    Fetched by id rather than found among the rows on screen: a failed write
    is the kind of thing someone pastes into a chat, and a link is followed
    cold, from a page nobody has loaded (M2-13).
  */
  const writeQuery = useQuery({
    queryKey: ["settings", "channel-write", recordId],
    queryFn: () =>
      recordId === null ? null : data.settings.getChannelWrite(recordId),
    enabled: recordId !== null,
  });

  const filter = filterForStatusTab(statusTab);

  const writesQuery = useQuery({
    queryKey: ["settings", "channel-writes", statusTab, sort, limit, cursor],
    queryFn: () =>
      data.settings.listChannelWrites({ filter, sort, limit, cursor }),
  });

  // Every tab's count, not just the active one — a rejection count that only
  // appears once you have already filtered to rejections tells nobody
  // anything before they've clicked. Each is its own `limit: 1` read: only
  // `total` is wanted, and a write count is exactly the kind of query a real
  // backend can answer cheaply without materialising the rows behind it.
  const countsQuery = useQuery({
    queryKey: ["settings", "channel-writes", "counts"],
    queryFn: async () => {
      const [all, failed, pending] = await Promise.all([
        data.settings.listChannelWrites({ limit: 1 }),
        data.settings.listChannelWrites({
          filter: filterForStatusTab("failed"),
          limit: 1,
        }),
        data.settings.listChannelWrites({
          filter: filterForStatusTab("pending"),
          limit: 1,
        }),
      ]);
      const counts: ChannelWriteCounts = {
        all: all.total,
        failed: failed.total,
        pending: pending.total,
      };
      return counts;
    },
  });

  // Every actor is a user, however long ago they wrote — fetched once at a
  // size that covers the fixture's whole roster, the same approach
  // `AuditLog` takes for the same reason.
  const usersQuery = useQuery({
    queryKey: ["settings", "users", "all", "for-channel-write-names"],
    queryFn: () => data.settings.listUsers({ limit: 200 }),
  });

  if (writesQuery.isPending || usersQuery.isPending) return <TableSkeleton />;

  if (writesQuery.isError) {
    return (
      <LoadFailed
        message={writesQuery.error.message}
        onRetry={() => {
          void writesQuery.refetch();
        }}
      />
    );
  }
  if (usersQuery.isError) {
    return (
      <LoadFailed
        message={usersQuery.error.message}
        onRetry={() => {
          void usersQuery.refetch();
        }}
      />
    );
  }

  const actorNames = new Map(
    usersQuery.data.items.map((user) => [user.id, user.name]),
  );
  // A null actor is a system write — a reconcile sweep, not a person — so it
  // gets a plain label instead of falling through to the raw `null`.
  function nameOf(actorUserId: string | null): string {
    if (actorUserId === null) return "System";
    return actorNames.get(actorUserId) ?? actorUserId;
  }

  return (
    <div className="flex flex-col gap-3">
      {recordId !== null ? (
        <RecordDrawer title={writeQuery.data?.mutation ?? "Write"}>
          {writeQuery.data ? (
            <ChannelWriteDetail
              write={writeQuery.data}
              actorName={nameOf(writeQuery.data.actorUserId)}
            />
          ) : (
            <TableSkeleton rows={3} />
          )}
        </RecordDrawer>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {STATUS_TABS.map((tab) => {
          const count = countFor(countsQuery.data, tab.value);
          return (
            <Button
              key={tab.value}
              type="button"
              size="sm"
              variant="outline"
              className={cn(statusTab === tab.value && SELECTED_CHOICE)}
              aria-pressed={statusTab === tab.value}
              onClick={() => {
                setStatusTab(tab.value);
                setHistory([undefined]);
              }}
            >
              {tab.label}
              {count !== null ? ` (${count})` : ""}
            </Button>
          );
        })}
      </div>

      <div className="rounded-md border">
        <DataTable
          fields={CHANNEL_WRITE_FIELDS}
          rows={writesQuery.data.items}
          getRowId={(write) => write.id}
          project={projectChannelWrite}
          sort={sort}
          onToggleSort={(field, additive) => {
            setSort((current) => toggleSort(current, field, additive));
          }}
          onRowClick={(write) => {
            open(write.id);
          }}
          renderOverride={(field, write) => {
            if (field.name === "status") {
              return (
                <Badge
                  variant={statusBadgeVariant(write.status)}
                  className="font-normal"
                >
                  {STATUS_LABEL[write.status]}
                </Badge>
              );
            }
            if (field.name === "actorUserId") {
              return renderCell(field, nameOf(write.actorUserId));
            }
            return undefined;
          }}
          emptyState={
            statusTab === "all" ? (
              <NothingYet
                title="No writes yet"
                description="Nothing has crossed the channel boundary yet — every order tag, stock adjustment and HSN write the ERP attempts will show up here."
              />
            ) : (
              <NoMatches
                description={`No ${statusTab} writes.`}
                onClear={() => {
                  setStatusTab("all");
                  setHistory([undefined]);
                }}
              />
            )
          }
        />
      </div>

      {writesQuery.data.items.length > 0 ? (
        <Pagination
          page={writesQuery.data}
          limit={limit}
          onLimitChange={(next) => {
            setLimit(next);
            setHistory([undefined]);
          }}
          history={history}
          onNavigate={setHistory}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------- sync status

type HealthLevel = "green" | "amber" | "red";

interface Health {
  level: HealthLevel;
  reasons: string[];
}

/** `"90m"` below two hours, `"4h"` above — this is a health summary, not a stopwatch. */
function formatAge(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 120) return `${String(minutes)}m`;
  return `${String(Math.round(minutes / 60))}h`;
}

/**
 * Green when every resource's last sweep succeeded within two sweep
 * intervals and no webhook has failed in the last hour (#154) — amber for a
 * resource running stale or a webhook hiccup on an otherwise-healthy sync,
 * red for a resource actively failing, one that has never completed a sweep,
 * or sync not being configured at all. `raise` only ever moves towards red:
 * an amber reason found after a red one must not paper back over it.
 */
function computeHealth(status: SyncStatus): Health {
  const reasons: string[] = [];
  let level: HealthLevel = "green";
  const raise = (next: HealthLevel) => {
    if (next === "red" || level === "green") level = next;
  };

  if (!status.scheduler.enabled) {
    raise("red");
    reasons.push("Sync is not configured — Shopify credentials are missing.");
  }

  const staleAfterMs =
    status.scheduler.intervalMinutes > 0
      ? status.scheduler.intervalMinutes * 60_000 * 2
      : null;

  for (const resource of status.resources) {
    if (resource.lastStatus === "failed") {
      raise("red");
      reasons.push(
        `${resource.resource}: last sweep failed${
          resource.failureCount > 1
            ? ` (${String(resource.failureCount)} in a row)`
            : ""
        }.`,
      );
    } else if (resource.lastSuccessAt === null) {
      raise("red");
      reasons.push(`${resource.resource}: has never completed a sweep.`);
    } else if (staleAfterMs !== null) {
      const age = Date.now() - new Date(resource.lastSuccessAt).getTime();
      if (age > staleAfterMs) {
        raise("amber");
        reasons.push(
          `${resource.resource}: last success was ${formatAge(age)} ago — more than two sweep intervals.`,
        );
      }
    }
  }

  if (status.webhooks.failedLastHour > 0) {
    raise("amber");
    reasons.push(
      `${String(status.webhooks.failedLastHour)} webhook ${
        status.webhooks.failedLastHour === 1 ? "delivery" : "deliveries"
      } failed in the last hour.`,
    );
  }

  if (reasons.length === 0) {
    reasons.push(
      "Every resource's last sweep succeeded on schedule, and no webhook deliveries have failed in the last hour.",
    );
  }

  return { level, reasons };
}

const HEALTH_LABEL: Record<HealthLevel, string> = {
  green: "Healthy",
  amber: "Needs attention",
  red: "Failing",
};

/** Same emerald/amber/destructive tokens `OrderHistory.tsx` uses for the same three tones. */
const HEALTH_TONE: Record<HealthLevel, string> = {
  green: "border-transparent bg-tint-green text-tint-green-foreground",
  amber: "border-transparent bg-tint-yellow text-tint-yellow-foreground",
  red: "border-destructive/30 bg-destructive/10 text-destructive",
};

function HealthBanner({ status }: { status: SyncStatus }) {
  const health = computeHealth(status);
  return (
    <div
      className={`flex items-start gap-2 rounded-md border p-3 text-sm ${HEALTH_TONE[health.level]}`}
    >
      {health.level === "green" ? (
        <CircleCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
      ) : (
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
      )}
      <div>
        <p className="font-medium">{HEALTH_LABEL[health.level]}</p>
        <ul className="mt-1 space-y-0.5">
          {health.reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function titleCase(resource: string): string {
  const spaced = resource.replaceAll("_", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `"9.8s"` above a second, `"420ms"` below — a sweep is seconds, a webhook re-read is milliseconds. */
function formatDuration(ms: number | null): string | null {
  if (ms === null) return null;
  return ms < 1000 ? `${String(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function recordsSummary(resource: SyncResourceStatus): string {
  if (resource.lastRecords === null) return "—";
  const parts = [
    `${String(resource.lastRecords)} read`,
    `${String(resource.lastInserted ?? 0)} new`,
  ];
  if (resource.lastEvents !== null) {
    parts.push(`${String(resource.lastEvents)} events`);
  }
  return parts.join(" · ");
}

const OUTCOME_LABEL: Record<string, string> = {
  ok: "Succeeded",
  failed: "Failed",
  running: "Running",
};

function outcomeBadgeVariant(
  status: string | null,
): "secondary" | "destructive" | "outline" {
  if (status === "failed") return "destructive";
  if (status === "running" || status === null) return "outline";
  return "secondary";
}

/**
 * One `sync_state` row (#154, FR-SYN-7) — last run, outcome, records
 * touched, consecutive failures, next run, and the failure's own words.
 *
 * `nextRunAt` comes from the scheduler, not the row: every resource sweeps
 * in the same pass (`sync/sweep.ts`'s `runSweep`), so there is one next-run
 * time, not one per card.
 */
function SweepCard({
  resource,
  nextRunAt,
}: {
  resource: SyncResourceStatus;
  nextRunAt: string | null;
}) {
  const duration = formatDuration(resource.lastDurationMs);
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">
          {titleCase(resource.resource)}
        </CardTitle>
        <Badge
          variant={outcomeBadgeVariant(resource.lastStatus)}
          className="gap-1 font-normal"
        >
          {resource.lastStatus === "running" ? (
            <Loader2 className="size-3 animate-spin" />
          ) : resource.lastStatus === "failed" ? (
            <TriangleAlert className="size-3" />
          ) : (
            <CircleCheck className="size-3" />
          )}
          {resource.lastStatus
            ? (OUTCOME_LABEL[resource.lastStatus] ?? resource.lastStatus)
            : "Never run"}
        </Badge>
      </CardHeader>
      <CardContent className="grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <div className="text-muted-foreground text-xs">Last run</div>
          <div className="tabular-nums">
            {resource.lastStartedAt
              ? formatTimestamp(resource.lastStartedAt)
              : "Never"}
            {resource.lastFinishedAt
              ? ` – ${formatTimestamp(resource.lastFinishedAt)}`
              : ""}
            {duration ? ` (${duration})` : ""}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">Records touched</div>
          <div className="tabular-nums">{recordsSummary(resource)}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            Consecutive failures
          </div>
          <div className="tabular-nums">{resource.failureCount}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">Next run</div>
          <div className="tabular-nums">
            {nextRunAt ? formatTimestamp(nextRunAt) : "Not scheduled"}
          </div>
        </div>
        {resource.lastError ? (
          <div className="sm:col-span-2">
            <div className="text-muted-foreground text-xs">Last error</div>
            <div className="text-destructive">{resource.lastError}</div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Runs a sweep now (#154). Only rendered with `settings.sync.trigger` —
 * hiding it is a courtesy, `POST /api/sync/run` still checks the permission
 * itself (CLAUDE.md: permission checks are server-side, always).
 *
 * "Already running" is `ok: true` with `alreadyRunning: true`, not a
 * rejection — the advisory lock in `sync/sweep.ts` joined an in-flight sweep
 * rather than refusing this one, which is the point of the lock, not a
 * failure to report as one.
 */
function SyncNowButton() {
  const data = useDataSource();
  const permissions = usePermissions();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => data.settings.runSync(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["settings", "sync-status"],
      });
    },
  });

  if (!can(permissions, "settings.sync.trigger")) return null;

  return (
    <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-3">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? (
          <Loader2 className="animate-spin" />
        ) : (
          <RefreshCw />
        )}
        Sync now
      </Button>
      {mutation.data ? (
        mutation.data.ok ? (
          <p className="text-muted-foreground text-sm">
            {mutation.data.data.alreadyRunning
              ? "A sweep was already running — this joined it."
              : "Sweep started. Results land in the cards above once it finishes."}
          </p>
        ) : (
          <p className="text-destructive text-sm">
            {mutation.data.rejection.message}
          </p>
        )
      ) : mutation.isError ? (
        <p className="text-destructive text-sm">
          Could not reach the sync service. Try again.
        </p>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------- webhooks

const WEBHOOK_STATUS_ORDER: WebhookEventStatus[] = [
  "received",
  "processed",
  "duplicate",
  "skipped",
  "failed",
];

const WEBHOOK_STATUS_LABEL: Record<WebhookEventStatus, string> = {
  received: "Received",
  processed: "Processed",
  duplicate: "Duplicate",
  skipped: "Skipped",
  failed: "Failed",
};

/** Every topic this app subscribes to (`docs/shopify-webhooks.md`), for the topic filter. */
const KNOWN_TOPICS = [
  "orders/create",
  "orders/updated",
  "orders/cancelled",
  "orders/fulfilled",
  "orders/paid",
  "fulfillments/create",
  "fulfillments/update",
  "products/create",
  "products/update",
  "orders/delete",
  "products/delete",
  "inventory_levels/update",
  "locations/create",
  "locations/update",
  "locations/delete",
];

function eventBadgeVariant(
  status: WebhookEventStatus,
): "secondary" | "destructive" | "outline" {
  if (status === "failed") return "destructive";
  if (status === "received") return "outline";
  return "secondary";
}

type EventStatusFilter = "all" | WebhookEventStatus;

/**
 * Webhook delivery health (#154, FR-SYN-3/4): 24h counts by status, the most
 * recent delivery, and the deliveries themselves — defaulted to `failed`,
 * since that is the one status someone opens this section to read, with
 * `status` and `topic` filters to look past it.
 *
 * Plain rows rather than the shared `DataTable`: two optional equality
 * filters and four columns is simpler built directly than dressed up in a
 * field vocabulary nothing else needs (see `routes/sync.ts`'s `/events` for
 * the same call made on the API side).
 */
function WebhooksSection({ webhooks }: { webhooks: WebhookHealth }) {
  const data = useDataSource();
  const [statusFilter, setStatusFilter] = useState<EventStatusFilter>("failed");
  const [topicFilter, setTopicFilter] = useState<string>("all");
  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory(statusFilter, topicFilter);
  const cursor = history[history.length - 1];

  const query: SyncEventsQuery = {
    status: statusFilter === "all" ? undefined : statusFilter,
    topic: topicFilter === "all" ? undefined : topicFilter,
    limit,
    cursor,
  };

  const eventsQuery = useQuery({
    queryKey: [
      "settings",
      "sync-events",
      statusFilter,
      topicFilter,
      limit,
      cursor,
    ],
    queryFn: () => data.settings.listSyncEvents(query),
  });

  function resetToFirstPage() {
    setHistory([undefined]);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold">Webhooks</h2>
        <p className="text-muted-foreground text-sm">
          What Shopify has delivered to the webhook endpoint, newest first.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {WEBHOOK_STATUS_ORDER.map((status) => (
          <Badge key={status} variant="outline" className="font-normal">
            {WEBHOOK_STATUS_LABEL[status]} {webhooks.last24h[status]}
          </Badge>
        ))}
      </div>

      {webhooks.mostRecentAt === null ? (
        <p className="text-muted-foreground max-w-prose text-sm">
          No webhooks received yet. Shopify has not been told to send any — the
          subscriptions this endpoint expects are in docs/shopify-webhooks.md,
          and registering them against the store is issue #155.
        </p>
      ) : (
        <p className="text-muted-foreground text-sm">
          Most recent delivery: {formatTimestamp(webhooks.mostRecentAt)}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value as EventStatusFilter);
            resetToFirstPage();
          }}
        >
          <SelectTrigger size="sm" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {WEBHOOK_STATUS_ORDER.map((status) => (
              <SelectItem key={status} value={status}>
                {WEBHOOK_STATUS_LABEL[status]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={topicFilter}
          onValueChange={(value) => {
            setTopicFilter(value);
            resetToFirstPage();
          }}
        >
          <SelectTrigger size="sm" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All topics</SelectItem>
            {KNOWN_TOPICS.map((topic) => (
              <SelectItem key={topic} value={topic}>
                {topic}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {eventsQuery.isPending ? (
        <TableSkeleton rows={3} />
      ) : eventsQuery.isError ? (
        <LoadFailed
          message={eventsQuery.error.message}
          onRetry={() => {
            void eventsQuery.refetch();
          }}
        />
      ) : eventsQuery.data.items.length === 0 ? (
        <NoMatches
          description="No deliveries match this filter."
          onClear={() => {
            setStatusFilter("all");
            setTopicFilter("all");
            resetToFirstPage();
          }}
        />
      ) : (
        <div className="space-y-2">
          {eventsQuery.data.items.map((event) => (
            <div key={event.id} className="rounded-md border p-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs">{event.topic}</span>
                <Badge
                  variant={eventBadgeVariant(event.status)}
                  className="font-normal"
                >
                  {WEBHOOK_STATUS_LABEL[event.status]}
                </Badge>
              </div>
              <div className="text-muted-foreground mt-0.5 text-xs">
                {formatTimestamp(event.receivedAt)}
              </div>
              {event.error ? (
                <div className="text-destructive mt-1">{event.error}</div>
              ) : null}
              {event.reason ? (
                <div className="text-muted-foreground mt-1 text-xs">
                  {event.reason}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {eventsQuery.data && eventsQuery.data.items.length > 0 ? (
        <Pagination
          page={eventsQuery.data}
          limit={limit}
          onLimitChange={(next) => {
            setLimit(next);
            resetToFirstPage();
          }}
          history={history}
          onNavigate={setHistory}
        />
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------- sweep board

/**
 * The health summary and sweep cards (#154, FR-SYN-7) — real state, read
 * from `GET /api/sync/status`. One card per row `sync_state` holds, not a
 * fixed three: #149 adds `inventory` alongside `orders` and `products`, and
 * this renders whatever the API returns rather than naming resources here.
 */
function SweepBoard() {
  const data = useDataSource();
  const statusQuery = useQuery({
    queryKey: ["settings", "sync-status"],
    queryFn: () => data.settings.getSyncStatus(),
  });

  if (statusQuery.isPending) return <TableSkeleton rows={3} />;
  if (statusQuery.isError) {
    return (
      <LoadFailed
        message={statusQuery.error.message}
        onRetry={() => {
          void statusQuery.refetch();
        }}
      />
    );
  }

  const status = statusQuery.data;

  return (
    <div className="space-y-4">
      <HealthBanner status={status} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Sweeps</h2>
        <SyncNowButton />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {status.resources.map((resource) => (
          <SweepCard
            key={resource.resource}
            resource={resource}
            nextRunAt={status.scheduler.nextRunAt}
          />
        ))}
      </div>

      <WebhooksSection webhooks={status.webhooks} />
    </div>
  );
}

/**
 * Shopify sync status (#154, M8-12) and the channel write log (#270 item 3).
 *
 * FR-SYN's write-through and idempotency invariants (identity is
 * `(channel_id, external_id)`, a webhook redelivery or a backfill retry
 * changes nothing extra) are what the status board exists to make legible:
 * when a sweep gets stuck, "which resource, how far behind, what did it
 * say" needs to be readable from a page, not from a container log — see
 * CLAUDE.md's "Expected failures are rows, not logs." `SweepBoard` and
 * `WebhooksSection` are that board wired to `GET /api/sync/status` and
 * `GET /api/sync/events`; nothing here is invented data any more.
 *
 * The write log below it answers the same CLAUDE.md rule for every write the
 * ERP has actually attempted, not just resource-level sync health. The two
 * live in one screen rather than a second nav entry because §11.2's
 * navigation map has no section for failed writes, and both are the same
 * subject: the state of the channel boundary.
 *
 * One scrolling column rather than the fixed-height split the placeholder
 * board used: three sweep cards plus a webhook board is taller than a phone
 * screen, and a page that scrolls is simpler than three independently
 * bounded panes fighting for a fixed height under it.
 */
export function ShopifySync() {
  return (
    <div className="space-y-6 p-4">
      <SweepBoard />

      {/* Failed tracking checks (#527): the same glance as the sweeps. */}
      <div className="border-t pt-4">
        <TrackingProblems />
      </div>

      <div className="space-y-3 border-t pt-4">
        <div>
          <h2 className="text-sm font-semibold">Channel writes</h2>
          <p className="text-muted-foreground text-sm">
            What the ERP has attempted at Shopify, newest first — what, against
            what, when, by whom, and the channel's own words for anything it
            refused.
          </p>
        </div>
        <ChannelWriteLog />
      </div>
    </div>
  );
}
